import { TEXT_ALIGNMENT } from 'constants/fonts';
import { ChangeEvent, RefObject } from 'react';
import { AccessibleElement, Widget } from 'types/widget.types';

type FormProps = ShortQuestionProps | LongQuestionProps | SelectionFieldProps;

interface FormBaseProps {
  ref?: RefObject<HTMLLabelElement>;
  setWidgetState?: (state: any) => void;
  isLabelFocused?: boolean;
  isReadOnly?: boolean;
  storedValue?: string;
  disableTabbability?: boolean;
}

interface ShortQuestionProps extends FormBaseProps {}
interface LongQuestionProps extends FormBaseProps {}
interface SelectionFieldProps extends FormBaseProps {}

enum FontWeightOption {
  Normal = 'normal',
  Bold = 'bold',
}

enum TextDecorationOption {
  None = 'none',
  Underline = 'underline',
}

enum FontStyleOption {
  Normal = 'normal',
  Italic = 'italic',
}

enum LabelAlignment {
  start = 'start',
  center = 'center',
  end = 'end',
}

enum LabelPosition {
  Top = 'top',
  Left = 'left',
}

enum FormType {
  ShortQuestion = 'Short question',
  LongQuestion = 'Long question',
  SelectionField = 'Selection field',
}

enum InputType {
  Text = 'text',
  Email = 'email',
  Password = 'password',
  Date = 'date',
  Number = 'number',
  // Not really being used but just to classify Long question
  Textarea = 'textarea',
}

enum SelectionFieldType {
  Radio = 'radio',
  Checkbox = 'checkbox',
}

enum SelectionFieldLayoutType {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

type FormStyle = {
  // Only for Long text field to prevent label height overflow when resizing
  minWidth: number;

  lineHeight: string;
  fontWeight: FontWeightOption;
  fontStyle: FontStyleOption;
  textDecoration: TextDecorationOption;
  fontSize: number;
  fontFamily: string;
  textAlign: TEXT_ALIGNMENT;
  alignSelf: LabelAlignment;
  color: string;

  backgroundColor: string;
  borderRadius: number;
  borderColor: string;
  borderWidth: number;

  gap: number;
};

type FormField = {
  label: string;
  type: string;
  value: string;
  placeholder: string;
};

type SelectionFieldOption = {
  index: number;
  value: string;
};

type SelectionFieldData = {
  type: SelectionFieldType;
  layout: SelectionFieldLayoutType;
  options: SelectionFieldOption[];
  enableOther: boolean;
  enableMultipleSelection: boolean;
};

interface FormWidgetData extends Widget, AccessibleElement {
  type: FormType;
  style: FormStyle;
  submitButtonLabel: string;
  field: FormField;
  labelPosition: LabelPosition;
  textAreaHeightPx?: number;
  selectionFieldData?: SelectionFieldData;
}

interface FieldsProps {
  type: FormType;
  isLabelFocus: boolean;
}

interface ValueInputProps extends FieldsProps {}

type OtherFontStyle = {
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  fontWeight?: FontWeightOption;
  fontStyle?: FontStyleOption;
  textDecoration?: TextDecorationOption;
};

type OtherBoxStyle = {
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: number;
  borderWidth?: number;
};
interface OtherInputProps {
  value: string;
  boxStyle?: OtherBoxStyle;
  fontStyle?: OtherFontStyle;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
}

export type {
  FormWidgetData,
  FormProps,
  FormStyle,
  FormField,
  SelectionFieldData,
  ShortQuestionProps,
  LongQuestionProps,
  SelectionFieldProps,
  FormBaseProps,
  FieldsProps,
  ValueInputProps,
  OtherInputProps,
};
export {
  LabelAlignment,
  LabelPosition,
  FormType,
  InputType,
  SelectionFieldType,
  SelectionFieldLayoutType,
  FontWeightOption,
  FontStyleOption,
  TextDecorationOption,
};
